import { DragStart, DragUpdate, DropResult } from "@hello-pangea/dnd";
import { useCallback, useRef, useEffect, useState } from "react";

import { usePlayground } from "../../playground-store";

interface DragAndDropConfig {
  dragDisabled: boolean;
  draggingId: string | null;
  handleDragStart: (start: DragStart) => void;
  handleDragEnd: (result: DropResult) => void;
  handleDragUpdate: (result: DragUpdate) => void;
  droppableRef: React.MutableRefObject<HTMLDivElement | null>;
}

/**
 * Edge-scroll configuration.
 *
 * We listen for mouse/touch position during a drag via a document-level
 * listener (since @hello-pangea/dnd's `onDragUpdate` does NOT include
 * pointer coordinates). When the pointer is within `THRESHOLD` px of the
 * top or bottom of the scroll container we start scrolling.
 */
const AUTOSCROLL_THRESHOLD = 60;
const AUTOSCROLL_MAX_SPEED = 12;

const useMessageDragAndDrop = (): DragAndDropConfig => {
  const playground = usePlayground();
  const droppableRef = useRef<HTMLDivElement | null>(null);
  const autoscrollRaf = useRef<number | null>(null);
  const pointerY = useRef<number | null>(null);
  const isDragging = useRef(false);

  const [draggingId, setDraggingId] = useState<string | null>(null);

  const dragDisabled = playground.readonly || playground.isAIPromptAssistantLoading;

  // ── Pointer tracking for autoscroll ────────────────────────────────
  // We track the pointer position at the document level because
  // @hello-pangea/dnd doesn't expose clientOffset in its callbacks.

  useEffect(() => {
    const onPointerMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging.current) {
        return;
      }
      const clientY = "touches" in e ? (e.touches[0]?.clientY ?? null) : e.clientY;
      pointerY.current = clientY;
    };

    document.addEventListener("mousemove", onPointerMove, { passive: true });
    document.addEventListener("touchmove", onPointerMove, { passive: true });

    return () => {
      document.removeEventListener("mousemove", onPointerMove);
      document.removeEventListener("touchmove", onPointerMove);
    };
  }, []);

  // ── rAF-driven autoscroll loop ─────────────────────────────────────
  const startAutoscrollLoop = useCallback(() => {
    const tick = () => {
      const container = droppableRef.current;
      const y = pointerY.current;

      if (!container || y === null || !isDragging.current) {
        autoscrollRaf.current = null;
        return;
      }

      const rect = container.getBoundingClientRect();
      const relativeY = y - rect.top;
      const containerHeight = rect.height;

      let speed = 0;

      if (relativeY < AUTOSCROLL_THRESHOLD && container.scrollTop > 0) {
        // Distance into the threshold zone (0 at edge, 1 at boundary)
        const ratio = 1 - relativeY / AUTOSCROLL_THRESHOLD;
        speed = -Math.round(AUTOSCROLL_MAX_SPEED * ratio);
      } else if (
        relativeY > containerHeight - AUTOSCROLL_THRESHOLD &&
        container.scrollTop < container.scrollHeight - container.clientHeight
      ) {
        const ratio = 1 - (containerHeight - relativeY) / AUTOSCROLL_THRESHOLD;
        speed = Math.round(AUTOSCROLL_MAX_SPEED * ratio);
      }

      if (speed !== 0) {
        container.scrollTop += speed;
      }

      autoscrollRaf.current = requestAnimationFrame(tick);
    };

    // Kick off the loop
    if (autoscrollRaf.current === null) {
      autoscrollRaf.current = requestAnimationFrame(tick);
    }
  }, []);

  const stopAutoscrollLoop = useCallback(() => {
    if (autoscrollRaf.current !== null) {
      cancelAnimationFrame(autoscrollRaf.current);
      autoscrollRaf.current = null;
    }
    pointerY.current = null;
  }, []);

  // ── Drag callbacks ─────────────────────────────────────────────────

  const handleDragStart = useCallback(
    (start: DragStart) => {
      if (dragDisabled) {
        return;
      }
      isDragging.current = true;
      setDraggingId(start.draggableId);
      startAutoscrollLoop();
    },
    [dragDisabled, startAutoscrollLoop],
  );

  const handleDragUpdate = useCallback((_result: DragUpdate) => {
    // The autoscroll is pointer-driven via document-level pointer
    // tracking, so no action is needed in this callback. We keep it
    // wired so DragDropContext doesn't warn about a missing handler.
  }, []);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      isDragging.current = false;
      setDraggingId(null);
      stopAutoscrollLoop();

      if (!result.destination || dragDisabled) {
        return;
      }

      const { source, destination } = result;

      if (
        source.index >= playground.messages.length ||
        destination.index >= playground.messages.length ||
        source.index < 0 ||
        destination.index < 0
      ) {
        return;
      }

      if (source.index !== destination.index) {
        playground.reorderMessages(source.index, destination.index);
      }
    },
    [dragDisabled, playground, stopAutoscrollLoop],
  );

  // Clean up on unmount
  useEffect(
    () => () => {
      stopAutoscrollLoop();
      isDragging.current = false;
    },
    [stopAutoscrollLoop],
  );

  return {
    dragDisabled,
    draggingId,
    handleDragStart,
    handleDragEnd,
    handleDragUpdate,
    droppableRef,
  };
};

export default useMessageDragAndDrop;
